// El Salvador Hacienda DTE (Documento Tributario Electrónico) parser, ported
// from invoice-loader-react's src/parsers.ts.
//
// El Salvador's schema uses a flat, non-repeating field vocabulary
// (nitEmisor, nitReceptor, identificacion.tipoDte, ...), so — unlike Costa
// Rica's, where the same tag name is reused inside different parent elements
// — a flat whole-payload search is safe here. Company-linking (sale/purchase
// role, companyId) is intentionally not ported: this loader has no company
// list to link documents against.

export const DOCUMENT_TYPE_LABELS = {
  '01': 'Factura consumidor final',
  '03': 'Comprobante credito fiscal',
  '04': 'Nota de remision',
  '05': 'Nota de credito',
  '06': 'Nota de debito',
  '07': 'Comprobante de retencion',
  '08': 'Comprobante de liquidacion',
  '09': 'Documento contable de liquidacion',
  '11': 'Factura de exportacion',
  '14': 'Sujeto excluido',
  '15': 'Comprobante de donacion',
};

const DTE_ROOT_KEYS = ['documento', 'dte', 'dteJson', 'json', 'body', 'payload', 'document'];
const XML_TIPO_DTE_TAGS = ['tipoDte', 'TipoDte', 'TipoDTE', 'tipoDocumento'];

// ─── Public entry points ────────────────────────────────────────────────────

// Parses one JSON file's text. A file can contain a single DTE, an array of
// DTEs, a DTE nested in a wrapper object/string, and/or "evento de
// invalidación" (void) records naming a DTE elsewhere — all are collected.
// Returns the same {ok, data|error} shape as parseCostaRica.js's parser.
export function parseElSalvadorJson(text, fileName, importedAt = new Date().toISOString()) {
  try {
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new Error('JSON inválido o malformado.');
    }

    const dtes = findDtePayloads(parsed);
    const voids = findAnulacionEvents(parsed);
    if (!dtes.length && !voids.length) {
      if (isContingenciaEvent(parsed)) {
        throw new Error('Evento de contingencia del MH; no contiene DTE de venta o compra.');
      }
      throw new Error('No se encontró una estructura DTE con identificacion.tipoDte.');
    }

    // With a single DTE the reception response may live anywhere in the file
    // (wrapper objects); with several, only trust a response found inside
    // each DTE's own record to avoid attributing one document's sello to another.
    const fileResponse = dtes.length === 1 ? findReceptionResponse(parsed) : null;
    const meta = { fileName, importedAt, sourceKind: 'json', rawPreview: text.slice(0, 1200) };
    const invoices = dtes.map((dte, index) => normalizeDte(
      dte,
      fileResponse ?? findReceptionResponse(dte),
      meta,
      dtes.length > 1 ? `-${index}` : '',
    ));
    return { ok: true, data: { invoices, voids } };
  } catch (err) {
    return { ok: false, error: err.message, fileName };
  }
}

// Parses one XML file's text.
export function parseElSalvadorXml(text, fileName, importedAt = new Date().toISOString()) {
  try {
    // Some exports wrap a JSON DTE payload inside an XML envelope.
    const embedded = text.match(/\{[\s\S]*"identificacion"[\s\S]*\}/);
    if (embedded) {
      const jsonResult = parseElSalvadorJson(embedded[0], fileName, importedAt);
      if (jsonResult.ok && jsonResult.data.invoices.length) return jsonResult;
      // Otherwise fall through to real XML extraction below.
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'application/xml');
    if (doc.querySelector('parsererror')) throw new Error('XML inválido o malformado.');

    const read = (...names) => {
      for (const name of names) {
        const el = doc.getElementsByTagName(name)[0] ?? doc.getElementsByTagNameNS('*', name)[0];
        const value = el?.textContent?.trim();
        if (value) return value;
      }
      return '';
    };

    const dte = {
      identificacion: {
        tipoDte: read('tipoDte', 'TipoDte', 'TipoDTE', 'tipoDocumento'),
        fecEmi: read('fecEmi', 'FechaEmision', 'fechaEmision', 'fecha'),
        codigoGeneracion: read('codigoGeneracion', 'CodigoGeneracion', 'Clave'),
        numeroControl: read('numeroControl', 'NumeroControl', 'NumeroConsecutivo'),
      },
      emisor: {
        nit: read('nitEmisor', 'emisorNit', 'NitEmisor', 'NITEmisor'),
        nrc: read('nrcEmisor', 'emisorNrc', 'NRCEmisor'),
        nombre: read('nombreEmisor', 'emisorNombre', 'NombreEmisor'),
        nombreComercial: read('nombreComercialEmisor', 'NombreComercialEmisor'),
      },
      receptor: {
        nit: read('nitReceptor', 'receptorNit', 'NitReceptor', 'NITReceptor'),
        nrc: read('nrcReceptor', 'receptorNrc', 'NRCReceptor'),
        numDocumento: read('duiReceptor', 'numDocumentoReceptor', 'NumeroDocumentoReceptor'),
        nombre: read('nombreReceptor', 'receptorNombre', 'NombreReceptor'),
      },
      resumen: {
        totalNoSuj: numberFrom(read('totalNoSuj', 'TotalNoSujetas', 'totalNoSujetas')),
        totalExenta: numberFrom(read('totalExenta', 'TotalExentas')),
        totalGravada: numberFrom(read('totalGravada', 'TotalGravada', 'totalGravadas')),
        totalIva: numberFrom(read('totalIva', 'TotalIva', 'totalImpuesto', 'TotalImpuesto')),
        ivaPerci1: numberFrom(read('ivaPerci1', 'IvaPercibido')),
        ivaRete1: numberFrom(read('ivaRete1', 'IvaRetenido')),
        reteRenta: numberFrom(read('reteRenta', 'RentaRetenida')),
        montoTotalOperacion: numberFrom(read('montoTotalOperacion', 'MontoTotalOperacion')),
        totalPagar: numberFrom(read('totalPagar', 'TotalPagar', 'TotalComprobante')),
      },
    };

    if (!dte.identificacion.tipoDte) throw new Error('XML sin tipoDte reconocible.');
    const invoice = normalizeDte(dte, { selloRecibido: read('selloRecibido', 'SelloRecibido') }, {
      fileName,
      importedAt,
      sourceKind: 'xml',
      rawPreview: text.slice(0, 1200),
    });
    return { ok: true, data: { invoices: [invoice], voids: [] } };
  } catch (err) {
    return { ok: false, error: err.message, fileName };
  }
}

// Cheap detection check used by the schema dispatcher — takes an
// already-parsed Document so a batch of files only gets parsed once each.
export function isElSalvadorXmlDoc(doc) {
  return XML_TIPO_DTE_TAGS.some(name => Boolean(
    (doc.getElementsByTagName(name)[0] ?? doc.getElementsByTagNameNS('*', name)[0])?.textContent?.trim()
  ));
}

// Applies "evento de invalidación" (void) events against an invoice list,
// flipping matching invoices' status to 'voided'. Pure — call this after
// merging a batch's new invoices into the full accumulated list so a void
// event can retroactively flag an invoice imported in an earlier batch.
export function applyVoidEvents(invoices, voids) {
  if (!voids.length) return invoices;
  const voidedCodes = new Set(voids.map(v => v.generationCode));
  return invoices.map(invoice => (
    voidedCodes.has(invoice.generationCode) ? { ...invoice, status: 'voided' } : invoice
  ));
}

// Dedup by id (re-importing the same DTE is a no-op), newest first.
export function mergeElSalvadorInvoices(existing, incoming) {
  const seen = new Set(existing.map(inv => inv.id));
  const unique = incoming.filter(inv => !seen.has(inv.id));
  return [...existing, ...unique].sort((a, b) => b.date.localeCompare(a.date));
}

// ─── Internal parsing helpers ───────────────────────────────────────────────

// Detects MH "evento de invalidación" documents (anulacion-schema-v2): an
// identificacion with fecAnula plus a documento entry naming the voided DTE.
function findAnulacionEvents(value, depth = 0, out = [], visited = new Set()) {
  if (!value || typeof value !== 'object' || depth > 6 || visited.has(value)) return out;
  visited.add(value);
  if (Array.isArray(value)) {
    for (const item of value) findAnulacionEvents(item, depth + 1, out, visited);
    return out;
  }
  const record = value;
  const identificacion = objectAt(record, 'identificacion');
  const documento = objectAt(record, 'documento');
  const fecAnula = stringAt(identificacion, 'fecAnula');
  const voidedCode = stringAt(documento, 'codigoGeneracion');
  if (fecAnula && voidedCode && !stringAt(identificacion, 'tipoDte')) {
    out.push({
      generationCode: voidedCode,
      date: normalizeDate(fecAnula),
      motivo: stringAt(objectAt(record, 'motivo'), 'motivoAnulacion'),
    });
    return out;
  }
  for (const child of Object.values(record)) {
    findAnulacionEvents(child, depth + 1, out, visited);
  }
  return out;
}

// MH "evento de contingencia" (contingencia-schema-v3): identificacion with a
// transmission date and a detalleDTE list — informational, not an invoice.
function isContingenciaEvent(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value;
  const identificacion = objectAt(record, 'identificacion');
  return Boolean(identificacion && stringAt(identificacion, 'fTransmision') && Array.isArray(record.detalleDTE));
}

function normalizeDte(dte, response, meta, fallbackIdSuffix = '') {
  const identificacion = objectAt(dte, 'identificacion');
  const emisor = objectAt(dte, 'emisor');
  const receptor = objectAt(dte, 'receptor');
  const resumen = objectAt(dte, 'resumen');
  const tipoDte = stringAt(identificacion, 'tipoDte');
  const generationCode = stringAt(identificacion, 'codigoGeneracion') || stringAt(dte, 'codigoGeneracion');
  const controlNumber = stringAt(identificacion, 'numeroControl') || stringAt(dte, 'numeroControl');
  const date = stringAt(identificacion, 'fecEmi') || stringAt(identificacion, 'fechaEmi') || stringAt(dte, 'fecha');
  const receptionSeal = stringAt(response, 'selloRecibido') || stringAt(dte, 'selloRecibido') || stringAt(dte, 'receptionSeal');

  // Line items straight from cuerpoDocumento, mirroring the columns the
  // desktop app shows (numItem, descripcion, cantidad, precioUni,
  // ventaGravada, ivaItem). FSE (14) names its amount "compra".
  const cuerpo = Array.isArray(dte.cuerpoDocumento) ? dte.cuerpoDocumento : [];
  const items = cuerpo.slice(0, 200).flatMap((raw, index) => {
    if (!raw || typeof raw !== 'object') return [];
    const item = raw;
    return [{
      numItem: numAt(item, 'numItem') || index + 1,
      description: stringAt(item, 'descripcion'),
      quantity: numAt(item, 'cantidad') || 1,
      unitPrice: numAt(item, 'precioUni'),
      taxedAmount: numAt(item, 'ventaGravada', 'compra'),
      iva: 'ivaItem' in item ? numAt(item, 'ivaItem') : undefined,
    }];
  });

  const warnings = [];
  if (!generationCode) warnings.push('No se encontró codigoGeneracion.');
  if (!controlNumber) warnings.push('No se encontró numeroControl.');
  if (!receptionSeal) warnings.push('No se encontró selloRecibido; Hacienda lo solicita en algunos anexos.');
  if (!date) warnings.push('No se encontró fecha de emisión.');

  const totalNoSuj = numAt(resumen, 'totalNoSuj', 'totalNoSujetas');
  const totalExenta = numAt(resumen, 'totalExenta', 'totalExentas');
  const totalGravada = numAt(resumen, 'totalGravada', 'totalGravadas');
  const totalIva = resolveIva(resumen);
  // Later aliases cover schema variants: totalCompra (FSE 14), valorTotal
  // (donación 15), totalSujetoRetencion (retención 07).
  const montoTotalOperacion = numAt(resumen, 'montoTotalOperacion', 'subTotalVentas', 'totalCompra', 'valorTotal', 'totalSujetoRetencion');
  const totalPagar = numAt(resumen, 'totalPagar', 'totalComprobante', 'total', 'valorTotal');

  return {
    id: generationCode || `${meta.fileName}-${meta.importedAt}${fallbackIdSuffix}`,
    fileName: meta.fileName,
    importedAt: meta.importedAt,
    sourceKind: meta.sourceKind,
    status: detectStatus(dte, response),
    tipoDte,
    tipoDteLabel: DOCUMENT_TYPE_LABELS[tipoDte] ?? `DTE ${tipoDte || 'sin tipo'}`,
    date: normalizeDate(date),
    generationCode,
    controlNumber,
    receptionSeal,
    issuer: {
      name: stringAt(emisor, 'nombre'),
      commercialName: stringAt(emisor, 'nombreComercial'),
      nit: stringAt(emisor, 'nit') || stringAt(emisor, 'numDocumento'),
      nrc: stringAt(emisor, 'nrc'),
      dui: stringAt(emisor, 'dui'),
    },
    receiver: {
      name: stringAt(receptor, 'nombre'),
      commercialName: stringAt(receptor, 'nombreComercial'),
      nit: stringAt(receptor, 'nit') || stringAt(receptor, 'numDocumento'),
      nrc: stringAt(receptor, 'nrc'),
      dui: stringAt(receptor, 'dui') || stringAt(receptor, 'numDocumento'),
    },
    totals: {
      noSujetas: totalNoSuj,
      exentas: totalExenta,
      gravadas: totalGravada,
      iva: totalIva,
      ivaPercibido: numAt(resumen, 'ivaPerci1', 'ivaPercibido', 'ivaPerci'),
      ivaRetenido: numAt(resumen, 'ivaRete1', 'ivaRetenido', 'totalIVAretenido'),
      rentaRetenida: numAt(resumen, 'reteRenta', 'rentaRetenida'),
      totalOperacion: montoTotalOperacion || totalNoSuj + totalExenta + totalGravada,
      totalPagar: totalPagar || montoTotalOperacion + totalIva,
    },
    rawPreview: meta.rawPreview,
    warnings,
    items,
  };
}

// Collects EVERY DTE payload in the document, descending into arrays and
// stringified-JSON wrapper keys. Files exported in batches ("[{...},{...}]" or
// {"documentos": [...]}) would otherwise import only the first DTE or fail outright.
function findDtePayloads(value, depth = 0, out = [], visited = new Set()) {
  if (!value || typeof value !== 'object' || depth > 6 || visited.has(value)) return out;
  visited.add(value);

  if (Array.isArray(value)) {
    for (const item of value) findDtePayloads(item, depth + 1, out, visited);
    return out;
  }

  const record = value;
  const identificacion = objectAt(record, 'identificacion');
  if (stringAt(identificacion, 'tipoDte')) {
    out.push(record);
    return out;
  }

  for (const key of DTE_ROOT_KEYS) {
    const child = record[key];
    if (typeof child === 'string' && /^[[{]/.test(child.trim())) {
      try {
        findDtePayloads(JSON.parse(child), depth + 1, out, visited);
      } catch {
        // Ignore and keep searching.
      }
    }
  }

  for (const child of Object.values(record)) {
    findDtePayloads(child, depth + 1, out, visited);
  }
  return out;
}

function findReceptionResponse(value, depth = 0) {
  if (!value || typeof value !== 'object' || depth > 6) return null;
  const record = value;
  if (stringAt(record, 'selloRecibido')) return record;
  for (const key of ['responseMH', 'ResponseMH', 'respuestaHacienda', 'mhResponse', 'recepcion', 'consulta']) {
    const found = findReceptionResponse(record[key], depth + 1);
    if (found) return found;
  }
  for (const child of Object.values(record)) {
    if (typeof child === 'object') {
      const found = findReceptionResponse(child, depth + 1);
      if (found) return found;
    }
  }
  return null;
}

function detectStatus(dte, response) {
  const joined = [
    stringAt(dte, 'estado'),
    stringAt(response, 'estado'),
    stringAt(response, 'estadoDoc'),
    stringAt(response, 'descripcionMsg'),
  ].join(' ').toLowerCase();
  if (joined.includes('anulad') || joined.includes('invalid')) return 'voided';
  if (joined.includes('borrador') || joined.includes('draft')) return 'draft';
  if (joined.includes('rechaz')) return 'unknown';
  return 'completed';
}

function resolveIva(resumen) {
  const direct = numAt(resumen, 'totalIva', 'iva', 'totalImpuesto');
  if (direct) return direct;
  const tributos = resumen?.tributos;
  if (!Array.isArray(tributos)) return 0;
  return tributos.reduce((sum, item) => {
    if (!item || typeof item !== 'object') return sum;
    const code = stringAt(item, 'codigo');
    if (code === '20' || code.toLowerCase().includes('iva')) return sum + numAt(item, 'valor', 'monto');
    return sum;
  }, 0);
}

function objectAt(record, key) {
  const value = record?.[key];
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

function stringAt(record, key) {
  const value = record?.[key];
  if (value === undefined || value === null) return '';
  return String(value).trim();
}

function numAt(record, ...keys) {
  // Return the value of the first key that is actually present and numeric.
  // A legitimate 0 is a valid value and must NOT be treated as "missing", or
  // the scan falls through to unrelated aliases/fallbacks and fabricates totals.
  if (!record) return 0;
  for (const key of keys) {
    if (!(key in record)) continue;
    const value = record[key];
    if (value === null || value === undefined || value === '') continue;
    const numeric = typeof value === 'number' ? value : numberFrom(String(value));
    if (Number.isFinite(numeric)) return numeric;
  }
  return 0;
}

function numberFrom(value) {
  let s = String(value ?? '').trim();
  if (!s) return 0;
  const hasDot = s.includes('.');
  const hasComma = s.includes(',');
  if (hasDot && hasComma) {
    // The right-most separator is the decimal point; the other groups thousands.
    if (s.lastIndexOf(',') > s.lastIndexOf('.')) {
      s = s.replace(/\./g, '').replace(',', '.'); // 1.234,56 -> 1234.56
    } else {
      s = s.replace(/,/g, ''); // 1,234.56 -> 1234.56
    }
  } else if (hasComma) {
    // Only commas: treat as a decimal separator when it looks like one
    // (single comma with <=2 trailing digits), otherwise as thousands grouping.
    const parts = s.split(',');
    if (parts.length === 2 && parts[1].length <= 2) {
      s = `${parts[0]}.${parts[1]}`; // 1234,56 -> 1234.56
    } else {
      s = s.replace(/,/g, ''); // 1,234,567 -> 1234567
    }
  }
  s = s.replace(/[^0-9.+-]/g, '');
  const parsed = Number(s);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeDate(value) {
  if (!value) return '';
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  const slash = value.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return `${slash[3]}-${slash[2]}-${slash[1]}`;
  return value.slice(0, 10);
}
